import React from 'react';
import {
    FaDatabase,
    FaLaptopCode,
    FaSearch,
    FaBoxes,
    FaSortAmountDown,
    FaTrophy
} from 'react-icons/fa';

export const paperPlusMetadata = {
    id: 'paper-plus',
    title: "Sales Performance Analysis",
    company: "PaperPlus Solutions",
    category: "Sales Analytics",
    cardDescription: "Analyze sales performance for a B2B company. Identify top-performing reps, growth regions, and customer trends.",
    learningObjectives: [
        "Multi-Table JOINS",
        "Business Reporting",
        "Grouping & Aggregation",
        "Revenue Analysis"
    ],
    scenario: (
        <div className="space-y-8">
            {/* Hero Section - Clean, Minimalist, Website Theme */}
            <div className="bg-white dark:bg-[#0f172a] rounded-xl border border-gray-100 dark:border-gray-800 p-8 shadow-sm relative overflow-hidden group hover:border-orange-500/20 transition-all duration-500">
                <div className="relative z-10">
                    <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4 tracking-tight">
                        PaperPlus <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">Analytics</span>
                    </h3>

                    <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-lg max-w-3xl font-light">
                        You have joined as a <strong className="text-gray-900 dark:text-white font-semibold">Sales Analyst</strong> at PaperPlus Solutions,
                        a B2B company supplying office materials and printing services.
                        Your Sales Director needs immediate insights on rep performance, customer trends, and regional growth for the upcoming Q3 strategy meeting.
                    </p>
                </div>
                {/* Subtle Brand Accent */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-500/5 to-red-500/5 rounded-full blur-2xl -mr-10 -mt-10"></div>
            </div>

            {/* Schema Grid - Strictly Professional, No Emojis */}
            <div>
                <h4 className="text-xs font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-4 pl-1">
                    Data Architecture
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {[
                        { table: 'regions', desc: 'Territories & Locations' },
                        { table: 'sales_reps', desc: 'Sales Team Members' },
                        { table: 'accounts', desc: 'Customer Accounts' },
                        { table: 'orders', desc: 'Transaction Records' }
                    ].map((item) => (
                        <div key={item.table} className="bg-gray-50 dark:bg-[#162032] p-4 rounded-lg border border-gray-100 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-600 transition-colors">
                            <div className="font-mono font-bold text-orange-600 dark:text-orange-400 text-sm mb-1">
                                {item.table}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                {item.desc}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    ),
    introduction: ``,
    hasSQL: true
};

export const paperPlusChallenges = [
    {
        id: 'explore-accounts',
        title: 'Account Portfolio Overview',
        question: `- Retrieve all columns from the \`accounts\` table`,
        keyConcepts: ['SELECT', 'FROM', '*', 'LIMIT', 'data exploration'],
        defaultQuery: `-- TIP: SELECT * returns all columns - great for exploring a new table
-- Try: SELECT * FROM accounts`,
        expectedColumns: [
            { name: 'account_id', type: 'INTEGER' },
            { name: 'account_name', type: 'TEXT' },
            { name: 'industry', type: 'TEXT' },
            { name: 'sales_rep_id', type: 'INTEGER' },
        ],
        businessRequest: `Marcus needs a raw export of the complete customer portfolio for the upcoming Q3 strategy meeting. He wants to see every account PaperPlus manages.`,
        concept: `**SELECT and FROM**

\`SELECT\` picks which columns to display. \`FROM\` names the table to query. These two clauses are the minimum required for any SQL statement.

\`\`\`sql
SELECT * FROM table_name
\`\`\`

- \`SELECT *\` means "every column" - ideal for initial exploration.
- In production, list specific columns for better performance and stability when table structures change.`,

        context: `**The Business Request**

Marcus needs a raw export of the entire customer portfolio for the upcoming strategy meeting. He wants to see every account PaperPlus manages to understand the scope of the business.

**The Concept**

The \`SELECT\` statement is the foundation of all SQL queries. It retrieves data from tables.

- \`SELECT *\` means "give me all columns"
- \`FROM table_name\` specifies which table to query

**The Syntax**

\`\`\`sql
SELECT * FROM table_name
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

Write a query to retrieve all columns from the \`accounts\` table.

</div>`,
        hint: `**💭 Pattern to Use:**

Every SQL query needs two parts:

\`\`\`sql
SELECT what_you_want
FROM where_to_get_it
\`\`\`

**For this question:**
- What do you want? All columns (there's a special symbol for "all")
- Where is it? The \`accounts\` table

**Remember:** The asterisk (\`*\`) means "everything"`,
        hint2: `**⚡ The Answer:**

\`\`\`sql
SELECT * FROM accounts
\`\`\`

- \`SELECT *\` = give me all columns
- \`FROM accounts\` = from the accounts table`,
        solution: `SELECT * 
FROM accounts`,
        tip: `SELECT * is great for exploration, but in production code, always specify exact columns you need (e.g., SELECT account_id, account_name, industry). This makes queries faster and prevents breaking changes when table structure changes.`,
        explanation: `**Excellent! You've explored the customer portfolio.**

**What Just Happened:**

\`SELECT *\` retrieved all columns from the \`accounts\` table. The database returned every row with all 4 columns.

**What You're Seeing:**

- **account_id:** Unique identifier for each customer
- **account_name:** Company name
- **industry:** Sector (Retail, Technology, Healthcare, Finance)
- **sales_rep_id:** Which sales rep manages this account

**Business Context:**

Understanding your customer base is foundational:
- How many total accounts PaperPlus manages
- Industry diversity in the portfolio
- Account distribution across sales reps

**How SELECT Works:**

\`\`\`sql
SELECT [columns]  -- What to show
FROM [table]      -- Where to get it
\`\`\`

**Pro Tip:** \`SELECT *\` is great for exploration. In production, always specify columns (\`SELECT account_id, account_name\`) for better performance.`,
        concepts: [
            {
                icon: <FaDatabase className="text-3xl text-[#2563eb]" />,
                title: 'SELECT',
                description: 'Specifies which columns to retrieve. Use * for all columns, or list specific column names separated by commas.'
            },
            {
                icon: <FaSearch className="text-3xl text-[#f97316]" />,
                title: 'FROM',
                description: 'Specifies which table to query. Every SELECT statement needs a FROM clause to tell SQL where to get the data.'
            }
        ]

    },
    {
        id: 'find-high-value-industry',
        title: 'Technology Sector Accounts',
        question: `- Retrieve all accounts where the \`industry\` column equals \`'Technology'\``,
        keyConcepts: ['WHERE', '=', 'text filtering', 'single quotes for text values'],
        defaultQuery: `-- TIP: WHERE filters rows to only those matching a condition
-- WHERE column = 'value'  (text values need single quotes)`,
        expectedColumns: [
            { name: 'account_id', type: 'INTEGER' },
            { name: 'account_name', type: 'TEXT' },
            { name: 'industry', type: 'TEXT' },
            { name: 'sales_rep_id', type: 'INTEGER' },
        ],
        businessRequest: `The sales team is launching a focused campaign on the Technology sector this quarter. Marcus needs a list of every technology company to assign to specialised reps.`,
        concept: `**WHERE Clause**

\`WHERE\` filters rows before they are returned. Each row is tested against the condition - only rows where it evaluates to \`true\` are kept.

\`\`\`sql
SELECT columns
FROM table_name
WHERE column_name = 'value'
\`\`\`

- Text values must be wrapped in single quotes: \`'Technology'\`.
- Numbers do not use quotes: \`WHERE amount > 10000\`.
- Multiple conditions: \`WHERE col1 = 'A' AND col2 > 5\`.`,

        context: `**The Business Request**

The sales team is targeting the Technology sector this quarter for a focused campaign. Marcus needs a list of all technology companies to assign to specialized sales reps.

**The Concept**

The \`WHERE\` clause filters which rows are returned. It evaluates each row against a condition:

- **True?** Keep the row
- **False?** Discard it

**The Syntax**

\`\`\`sql
SELECT columns
FROM table_name
WHERE column_name = 'value'
\`\`\`

**Critical Detail:** Text values must be wrapped in single quotes (e.g., \`'Technology'\`). Numbers do not need quotes.

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

Retrieve all accounts where the \`industry\` column equals \`'Technology'\`.

</div>`,
        hint: `**💭 Pattern to Use:**

To filter rows, add a WHERE clause after FROM:

\`\`\`sql
SELECT columns
FROM table_name
WHERE column_name = 'value'
\`\`\`

**For this question:**
- Start with your SELECT * FROM accounts query
- Add WHERE to filter
- Which column contains the industry? 
- What value are you looking for?

**Important:** Text values need single quotes!`,
        hint2: `**⚡ The Answer:**

\`\`\`sql
SELECT * FROM accounts
WHERE industry = 'Technology'
\`\`\`

- WHERE filters rows
- industry = 'Technology' is the condition
- Text needs single quotes!`,
        solution: `SELECT * 
FROM accounts 
WHERE industry = 'Technology'`,
        tip: `You can filter by multiple industries using IN: WHERE industry IN ('Technology', 'Finance', 'Healthcare'). This is cleaner than multiple OR conditions.`,
        explanation: `**Filtered Like a Pro!**

**What Just Happened:**

SQL evaluated every row: "Is \`industry\` = 'Technology'?" Only matching rows were returned.

**Business Impact:**

Marcus can now:
- Create targeted tech marketing campaigns
- Assign tech-specialized reps to these accounts
- Track technology sector performance separately

**How WHERE Works:**

1. **FROM:** Load all rows from accounts
2. **WHERE:** Filter: keep only Technology rows
3. **SELECT:** Return the matching columns

**Common WHERE Patterns:**

- **Text match:** \`WHERE industry = 'Technology'\`
- **Numeric comparison:** \`WHERE amount > 10000\`
- **Date filter:** \`WHERE order_date >= '2024-01-01'\`
- **Multiple values:** \`WHERE industry IN ('Technology', 'Finance')\`
- **Pattern matching:** \`WHERE name LIKE 'A%'\`

**Pro Tip:** Text values need single quotes. Numbers and column names never need quotes.`,
        concepts: [
            {
                icon: <FaSearch className="text-3xl text-[#f97316]" />,
                title: 'WHERE',
                description: 'Filters rows based on conditions. Only rows that match the condition are returned in the results.'
            },
            {
                icon: <FaLaptopCode className="text-3xl text-[#10b981]" />,
                title: 'Text Values',
                description: "Text values must be enclosed in single quotes: WHERE industry = 'Technology'. Numbers don't need quotes."
            }
        ]
    },
    {
        id: 'top-reps-by-accounts',
        title: 'Sales Rep Workload Analysis',
        question: `- From \`accounts\`, count how many accounts each rep owns
- Return \`sales_rep_id\` and \`account_count\` for the top 5 reps, ordered by count descending`,
        keyConcepts: ['GROUP BY', 'COUNT()', 'ORDER BY', 'DESC', 'LIMIT', 'Top N Pattern'],
        defaultQuery: `-- TIP: COUNT(*) counts rows, GROUP BY groups them, ORDER BY sorts
-- SELECT col, COUNT(*) AS cnt FROM table GROUP BY col ORDER BY cnt DESC`,
        expectedColumns: [
            { name: 'sales_rep_id', type: 'INTEGER' },
            { name: 'account_count', type: 'INTEGER' },
        ],
        businessRequest: `Marcus suspects some reps are carrying far more accounts than others. He wants the top 5 most-loaded reps so he can assign support staff before the Q3 push.`,
        concept: `**The Top-N Pattern**

Combine \`GROUP BY\`, \`COUNT(*)\`, \`ORDER BY DESC\`, and \`LIMIT\` to rank categories by a metric.

\`\`\`sql
SELECT category_column, COUNT(*) AS count
FROM table
GROUP BY category_column
ORDER BY count DESC
LIMIT N
\`\`\`

- \`GROUP BY\` collapses all rows with the same value into one summary row.
- \`COUNT(*)\` counts how many rows fell into each group.
- \`ORDER BY count DESC\` ranks highest first; \`LIMIT 5\` returns only the top 5.

*SQL execution order: FROM -> GROUP BY -> SELECT -> ORDER BY -> LIMIT. ORDER BY runs after SELECT, which is why you can order by an alias.*`,

        context: `**The Business Request**

Marcus suspects some reps are overwhelmed while others have capacity. He wants to see the top 5 most overloaded reps so he can assign them support staff before the Q3 push.

**The Concept**

The "Top N" pattern is one of the most common analytical queries. It combines:
1. **GROUP BY** to collapse rows into summary groups
2. **COUNT(*)** to calculate a metric per group
3. **ORDER BY DESC** to rank from highest to lowest
4. **LIMIT N** to show only the top results

**The Syntax**

\`\`\`sql
SELECT category_column, COUNT(*) AS count
FROM table
GROUP BY category_column
ORDER BY count DESC
LIMIT N
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

Show the top 5 sales reps by account count. Display \`sales_rep_id\` and \`account_count\`, sorted from highest to lowest.

</div>`,
        hint: `**💭 Pattern to Use:**

Build this step-by-step:

1. **SELECT**: sales_rep_id and COUNT(*) AS account_count
2. **FROM**: accounts table
3. **GROUP BY**: sales_rep_id (to collapse by rep)
4. **ORDER BY**: account_count DESC (highest first)
5. **LIMIT**: 5 (only top 5)

**Golden Rule:** Every column in SELECT must be in GROUP BY or be an aggregate function.`,
        hint2: `**⚡ The Answer:**

\`\`\`sql
SELECT sales_rep_id, COUNT(*) AS account_count
FROM accounts
GROUP BY sales_rep_id
ORDER BY account_count DESC
LIMIT 5
\`\`\``,
        solution: `SELECT sales_rep_id, COUNT(*) AS account_count 
FROM accounts 
GROUP BY sales_rep_id 
ORDER BY account_count DESC 
LIMIT 5`,
        tip: `SQL execution order: FROM → WHERE → GROUP BY → HAVING → SELECT → ORDER BY → LIMIT. Even though you write SELECT first, it actually runs fifth! This is why you can ORDER BY an alias.`,
        explanation: `**The "Top N" Pattern Mastered!**

**What Just Happened:**

1. **FROM accounts:** Load all accounts
2. **GROUP BY sales_rep_id:** Group by rep
3. **COUNT(*):** Count accounts per rep
4. **ORDER BY count DESC:** Sort highest first
5. **LIMIT 5:** Return only top 5

**Business Actions:**

Marcus can now:
- Redistribute accounts to less-busy reps
- Assign junior reps to shadow overloaded performers
- Provide administrative support to reduce workload

**How GROUP BY + ORDER BY + LIMIT Works:**

This is THE most common analyst query: "Show me the top N by some metric."

\`\`\`sql
SELECT dimension, AGGREGATE(metric)
FROM table
GROUP BY dimension
ORDER BY AGGREGATE(metric) DESC
LIMIT N
\`\`\`

**SQL Execution Order:**

FROM → WHERE → GROUP BY → HAVING → SELECT → ORDER BY → LIMIT

Even though you write SELECT first, it actually runs fifth! This is why you can ORDER BY an alias.`,
        concepts: [
            {
                icon: <FaBoxes className="text-3xl text-[#2563eb]" />,
                title: 'GROUP BY + COUNT(*)',
                description: 'Collapses rows into groups and counts items per group. The foundation of business reporting.'
            },
            {
                icon: <FaSortAmountDown className="text-3xl text-[#f97316]" />,
                title: 'ORDER BY + LIMIT',
                description: 'Sorts results and restricts row count. Combined with GROUP BY, this creates "Top N" queries.'
            }
        ]
    },
    {
        id: 'industry-revenue',
        title: 'Industry Revenue Breakdown',
        question: `- Join \`orders\` to \`accounts\` on \`account_id\` and group by \`industry\`
- Return \`industry\` and \`total_revenue\`, ordered from highest to lowest`,
        keyConcepts: ['JOIN', 'GROUP BY', 'SUM()', 'Multi-table aggregation'],
        defaultQuery: `-- TIP: JOIN accounts to orders so you can GROUP BY industry
-- FROM orders JOIN accounts ON orders.account_id = accounts.account_id`,
        expectedColumns: [
            { name: 'industry', type: 'TEXT' },
            { name: 'total_revenue', type: 'DECIMAL' },
        ],
        businessRequest: `The executive team is deciding where to focus sales efforts next quarter. Marcus needs to rank industries by total revenue so they can double down on high-performing sectors.`,
        concept: `**JOIN + GROUP BY + SUM**

Use \`JOIN\` to link a fact table (orders) to a dimension table (accounts), then aggregate by a dimension column.

\`\`\`sql
SELECT dimension.category, SUM(fact.amount) AS total
FROM fact_table
JOIN dimension_table ON fact.key = dimension.key
GROUP BY dimension.category
ORDER BY total DESC
\`\`\`

- \`SUM()\` adds up all numeric values in a column per group.
- Always use \`table.column\` syntax when joining to prevent ambiguity on shared column names.`,

        context: `**The Business Request**

The executive team is deciding where to focus sales efforts next quarter. Marcus needs to know which industries are generating the most revenue so they can double down on high-performing sectors.

**The Concept**

This query requires combining data from two tables before aggregating:
1. **JOIN** orders and accounts to connect revenue to industry
2. **GROUP BY** industry to aggregate by sector
3. **SUM()** to calculate total revenue per industry

**The Syntax**

\`\`\`sql
SELECT category, SUM(amount) AS total
FROM table1
JOIN table2 ON table1.id = table2.id
GROUP BY category
ORDER BY total DESC
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

Show total revenue by industry. Join \`orders\` with \`accounts\` (on \`account_id\`), group by \`industry\`, and sum \`total_amount_usd\`. Order by revenue (highest first).

</div>`,
        hint: `**💭 Pattern to Use:**

1. **FROM**: Start with orders (it has the revenue)
2. **JOIN**: Connect to accounts ON orders.account_id = accounts.account_id
3. **SELECT**: accounts.industry and SUM(orders.total_amount_usd)
4. **GROUP BY**: accounts.industry
5. **ORDER BY**: total revenue DESC`,
        hint2: `**⚡ The Answer:**

\`\`\`sql
SELECT accounts.industry, SUM(orders.total_amount_usd) AS total_revenue
FROM orders
JOIN accounts ON orders.account_id = accounts.account_id
GROUP BY accounts.industry
ORDER BY total_revenue DESC
\`\`\``,
        solution: `SELECT accounts.industry, SUM(orders.total_amount_usd) AS total_revenue
FROM orders
JOIN accounts ON orders.account_id = accounts.account_id
GROUP BY accounts.industry
ORDER BY total_revenue DESC`,
        tip: `When joining and aggregating, always use table.column syntax to avoid ambiguity. This makes your query readable and prevents errors when column names exist in multiple tables.`,
        explanation: `**Multi-Table Aggregation Complete!**

**What Just Happened:**

1. **JOIN orders to accounts:** Link revenue to companies
2. **GROUP BY industry:** Aggregate by sector
3. **SUM revenue:** Calculate total for each industry
4. **ORDER BY total:** Rank by revenue descending

**Business Insights:**

Marcus can now see:
- Which industries drive the most revenue
- Where to focus sales expansion
- Potential underperforming sectors to investigate

**How JOIN + GROUP BY Works:**

This pattern combines transactional data (orders) with dimensional data (accounts) to create categorized reports:

\`\`\`sql
SELECT dimension.category, SUM(fact.amount)
FROM fact_table
JOIN dimension_table ON fact.key = dimension.key
GROUP BY dimension.category
\`\`\`

**Common Aggregation Functions:**

- **SUM():** Total revenue, quantity
- **COUNT():** Number of orders, customers
- **AVG():** Average deal size, rating
- **MAX() / MIN():** Largest / smallest order

**Pro Tip:** Always use \`table.column\` syntax when joining to avoid ambiguity.`,
        concepts: [
            {
                icon: <FaDatabase className="text-3xl text-[#2563eb]" />,
                title: 'JOIN + GROUP BY',
                description: 'Combine data from multiple tables, then aggregate. The core pattern for business reporting dashboards.'
            },
            {
                icon: <FaTrophy className="text-3xl text-[#f97316]" />,
                title: 'SUM()',
                description: 'Adds up numeric values in a column. Essential for revenue, quantity, and budget calculations.'
            }
        ]
    },
    {
        id: 'revenue-by-quarter',
        title: 'Quarterly Revenue Trends',
        question: `- Use \`DATE_TRUNC\` to bucket \`order_date\` by quarter
- Return \`quarter\` and \`quarterly_revenue\`, ordered chronologically`,
        keyConcepts: ['DATE_TRUNC()', 'GROUP BY', 'SUM()', 'aggregation functions'],
        defaultQuery: `-- TIP: DATE_TRUNC('quarter', date_col) rounds a date down to quarter start
-- GROUP BY 1  is shorthand for GROUP BY the first SELECT expression`,
        expectedColumns: [
            { name: 'quarter', type: 'DATE' },
            { name: 'quarterly_revenue', type: 'DECIMAL' },
        ],
        businessRequest: `Marcus needs to present quarter-by-quarter revenue trends to the board. He wants clear evidence of whether sales are growing and if there is any seasonal variation.`,
        concept: `**DATE_TRUNC for Time-Series Analysis**

\`DATE_TRUNC\` rounds a date down to the start of the chosen time period, so all dates within that period share the same value and can be grouped together.

\`\`\`sql
SELECT DATE_TRUNC('quarter', date_column) AS quarter,
       SUM(amount) AS total
FROM table
GROUP BY quarter
ORDER BY quarter
\`\`\`

- \`'quarter'\` rounds to the first day of the quarter (e.g. 2024-02-15 -> 2024-01-01).
- Other valid periods: \`'month'\`, \`'week'\`, \`'year'\`.
- Order chronologically to display trends clearly.`,

        context: `**The Business Request**

Marcus needs to present quarterly revenue trends to the board. He wants to see whether sales are growing quarter-over-quarter and identify any seasonal patterns.

**The Concept**

Time-series analysis is critical for business intelligence. \`DATE_TRUNC\` rounds dates to the start of a period, allowing you to group data by day, week, month, quarter, or year.

**How DATE_TRUNC Works:**

- \`DATE_TRUNC('quarter', '2023-05-20')\` → 2023-04-01 (Q2 start)
- \`DATE_TRUNC('month', '2023-05-20')\` → 2023-05-01

**The Syntax**

\`\`\`sql
SELECT 
  DATE_TRUNC('quarter', date_column) AS quarter,
  SUM(amount) AS total
FROM table
GROUP BY quarter
ORDER BY quarter
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

Calculate total revenue per quarter from the \`orders\` table. Order results chronologically.

</div>`,
        hint: `**💭 Pattern to Use:**

To analyze data by time periods:

\`\`\`sql
SELECT 
  DATE_TRUNC('period', date_column) AS period_name,
  SUM(amount_column) AS total
FROM table_name
GROUP BY period_name
ORDER BY period_name
\`\`\`

**For this question:**
- What time period? (quarter)
- Which date column? (order_date)
- What are you summing? (total_amount_usd)
- Don't forget to GROUP BY the truncated date!

**Tip:** You can use the alias in GROUP BY and ORDER BY`,
        hint2: `**⚡ The Answer:**

\`\`\`sql
SELECT 
  DATE_TRUNC('quarter', order_date) AS quarter,
  SUM(total_amount_usd) AS quarterly_revenue
FROM orders
GROUP BY quarter
ORDER BY quarter
\`\`\`

- DATE_TRUNC extracts quarter
- GROUP BY quarter collapses by quarter
- SUM calculates total per quarter
- ORDER BY shows trends chronologically`,
        solution: `SELECT 
  DATE_TRUNC('quarter', order_date) AS quarter,
  SUM(total_amount_usd) AS quarterly_revenue
FROM orders
GROUP BY quarter
ORDER BY quarter`,
        tip: `Click "View as Chart" to visualize this time series! Line charts make trends much easier to spot than tables. Look for growth (upward trend), seasonality (repeating patterns), or anomalies (sudden drops).`,
        explanation: `**Quarterly Time-Series Report Created!**

**What Just Happened:**

1. **DATE_TRUNC:** Grouped dates into quarters
2. **SUM:** Aggregated revenue per quarter
3. **ORDER BY:** Sorted chronologically

**How DATE_TRUNC Works:**

Matches dates to the start of the period:
- **quarter:** 2024-02-15 -> 2024-01-01
- **month:** 2024-02-15 -> 2024-02-01
- **year:** 2024-02-15 -> 2024-01-01
- **week:** 2024-02-15 -> 2024-02-12 (Monday)

**Business Insights You Can Extract:**

- **Growth:** Is revenue increasing quarter over quarter?
- **Seasonality:** Do certain quarters consistently spike or dip?
- **Anomalies:** Did any quarter have unexpected changes?
- **Forecasting:** What's next quarter's projected revenue?

**Pro Tip:** Click "View as Chart" to visualize trends. Line charts make patterns much clearer than tables.`,
        concepts: [
            {
                icon: <FaDatabase className="text-3xl text-[#2563eb]" />,
                title: 'DATE_TRUNC()',
                description: 'Rounds dates to the start of a time period (day, week, month, quarter, year). Essential for grouping time-series data into reporting periods.'
            },
            {
                icon: <FaBoxes className="text-3xl text-[#10b981]" />,
                title: 'GROUP BY',
                description: 'Collapses rows with the same value into summary rows. Every column in SELECT must be in GROUP BY or be an aggregate function.'
            },
            {
                icon: <FaLaptopCode className="text-3xl text-[#f97316]" />,
                title: 'SUM()',
                description: 'Adds up all numeric values in a column. Perfect for calculating totals like revenue, sales, or quantities.'
            },
            {
                icon: <FaSearch className="text-3xl text-[#6b7280]" />,
                title: 'Other Aggregates',
                description: 'COUNT(*) counts rows, AVG() calculates average, MAX() finds highest value, MIN() finds lowest value. Use with GROUP BY for category summaries.'
            }
        ]
    },
    {
        id: 'rep-names-with-join',
        title: 'Sales Rep Performance Scorecard',
        question: `- Join \`orders\` → \`accounts\` → \`sales_reps\` to get rep names
- Return \`sales_rep_name\` and \`total_revenue\`, ordered from highest to lowest`,
        keyConcepts: ['INNER JOIN', 'ON', 'GROUP BY', 'SUM()', 'multi-table query'],
        defaultQuery: `-- TIP: JOIN sales_reps to orders to replace numeric IDs with real names
-- FROM orders JOIN sales_reps ON orders.sales_rep_id = sales_reps.sales_rep_id`,
        expectedColumns: [
            { name: 'sales_rep_name', type: 'TEXT' },
            { name: 'total_revenue', type: 'DECIMAL' },
        ],
        businessRequest: `Marcus needs individual rep performance for the upcoming quarterly reviews. The executive report requires actual rep names - not numeric IDs.`,
        concept: `**Chaining Multiple JOINs**

Follow the key relationships through the schema to reach the table you need.

\`\`\`sql
SELECT table3.name, SUM(table1.amount) AS total
FROM table1
JOIN table2 ON table1.id = table2.t1_id
JOIN table3 ON table2.id = table3.t2_id
GROUP BY table3.name
\`\`\`

**Table path for this query:**
\`orders.account_id -> accounts.account_id\`
\`accounts.sales_rep_id -> sales_reps.sales_rep_id\`

Think of each JOIN as building a bridge between tables using their shared key column.`,

        context: `**The Business Request**

Marcus needs to evaluate individual rep performance for quarterly reviews. He needs actual names-not just IDs-for the executive report.

**The Concept**

In a relational database, data is split across tables to eliminate redundancy. To combine information from multiple tables, you use \`JOIN\` clauses with matching keys.

**Table Relationships:**

\`\`\`
orders.account_id → accounts.account_id
accounts.sales_rep_id → sales_reps.sales_rep_id
\`\`\`

**The Syntax**

\`\`\`sql
SELECT table3.name, SUM(table1.amount) AS total
FROM table1
JOIN table2 ON table1.id = table2.id
JOIN table3 ON table2.id = table3.id
GROUP BY table3.name
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

Join \`orders\` → \`accounts\` → \`sales_reps\` to show each rep's name and their total revenue. Order by revenue (highest first).

</div>`,
        hint: `**💭 Pattern to Use:**

To join multiple tables and aggregate:

\`\`\`sql
SELECT table3.name_column, SUM(table1.amount_column) AS total
FROM table1
INNER JOIN table2 ON table1.key = table2.key
INNER JOIN table3 ON table2.key = table3.key
GROUP BY table3.name_column
ORDER BY total DESC
\`\`\`

**For this question:**
- Start FROM orders (has the revenue)
- JOIN to accounts (connects orders to reps)
- JOIN to sales_reps (has the names)
- What columns do you need to match for each JOIN?
- GROUP BY which column? (the one you're showing)
- What are you summing?

**Remember:** Use table.column syntax to avoid ambiguity!`,
        hint2: `**⚡ The Answer:**

\`\`\`sql
SELECT 
  sales_reps.sales_rep_name,
  SUM(orders.total_amount_usd) AS total_revenue
FROM orders
INNER JOIN accounts ON orders.account_id = accounts.account_id
INNER JOIN sales_reps ON accounts.sales_rep_id = sales_reps.sales_rep_id
GROUP BY sales_reps.sales_rep_name
ORDER BY total_revenue DESC
\`\`\`

Two JOINs bridge from orders to rep names through accounts.`,
        solution: `SELECT 
  sales_reps.sales_rep_name,
  SUM(orders.total_amount_usd) AS total_revenue
FROM orders
INNER JOIN accounts ON orders.account_id = accounts.account_id
INNER JOIN sales_reps ON accounts.sales_rep_id = sales_reps.sales_rep_id
GROUP BY sales_reps.sales_rep_name
ORDER BY total_revenue DESC`,
        tip: `When joining multiple tables, always think about the "path" from your starting table to your destination. Here: orders → accounts (via account_id) → sales_reps (via sales_rep_id).`,
        explanation: `**Three Tables Connected!**

**What Just Happened:**

1. **orders:** Revenue data
2. **→ accounts:** Link to account owner
3. **→ sales_reps:** Get rep's actual name
4. **GROUP BY + SUM:** Revenue per rep

**How Multi-Table JOINs Work:**

Follow the "chain" from source to destination:

\`\`\`sql
FROM orders                    -- Start with revenue
JOIN accounts ON ...           -- Bridge to account
JOIN sales_reps ON ...         -- Bridge to rep names
\`\`\`

**The Power of JOINs:**

- **Without JOINs:** "Rep 4 generated $50k" (Who is Rep 4?)
- **With JOINs:** "Sarah generated $50k" (Actionable!)

**Business Impact:**

Marcus can now:
- Rank reps by revenue performance
- Identify top performers for bonuses
- Spot underperformers who need coaching
- Set commission structures fairly

**Pro Tip:** Always think about the "path" from your starting table to your destination through shared keys.`,
        concepts: [
            {
                icon: <FaDatabase className="text-3xl text-[#2563eb]" />,
                title: 'INNER JOIN',
                description: 'Combines rows from two tables where they match. Syntax: INNER JOIN table2 ON table1.key = table2.key'
            },
            {
                icon: <FaBoxes className="text-3xl text-[#f97316]" />,
                title: 'Multiple JOINs',
                description: 'Chain JOINs to connect multiple tables. Each JOIN connects to the previous result using ON conditions.'
            }
        ]
    },
    {
        id: 'regional-performance',
        title: 'Regional Revenue Analysis',
        question: `- Join \`orders\` → \`accounts\` → \`sales_reps\` → \`regions\` and group by region
- Return \`region_name\`, \`total_revenue\`, \`order_count\`, and \`avg_order_value\`, ordered by total revenue descending`,
        keyConcepts: ['INNER JOIN', '4 tables', 'GROUP BY', 'SUM()', 'COUNT()', 'multi-metric analysis'],
        defaultQuery: `-- TIP: Chain JOINs: orders -> accounts -> regions to reach region_name
-- You can SELECT multiple aggregates (SUM, COUNT) in the same GROUP BY query`,
        expectedColumns: [
            { name: 'region_name', type: 'TEXT' },
            { name: 'total_revenue', type: 'DECIMAL' },
            { name: 'order_count', type: 'INTEGER' },
            { name: 'avg_order_value', type: 'DECIMAL' },
        ],
        businessRequest: `Regional VPs are competing for Q3 budget allocation. Marcus needs objective performance data across all four regions so resources go to territories based on actual results.`,
        concept: `**Four-Table JOIN with Multiple Metrics**

Chain 4 JOINs and calculate multiple aggregate functions in the same query to build a complete performance dashboard in a single statement.

\`\`\`sql
SELECT region.name,
       SUM(orders.amount) AS total,
       COUNT(orders.id) AS count,
       ROUND(AVG(orders.amount), 2) AS avg
FROM orders
JOIN accounts ON ...
JOIN sales_reps ON ...
JOIN regions ON ...
GROUP BY region.name
\`\`\`

- \`COUNT(orders.order_id)\` counts transactions per region (volume).
- \`ROUND(AVG(...), 2)\` formats the average order value to 2 decimal places.
- Combine SUM (revenue), COUNT (volume), and AVG (deal quality) for a complete picture.`,

        context: `**The Business Request**

Regional VPs compete for resources (budget, headcount, marketing support). Marcus needs objective data to make fair allocation decisions based on each region's actual performance.

**The Concept**

This query requires joining four tables in a chain and calculating multiple metrics simultaneously. This is a common pattern for executive dashboards.

**The Four-Table Chain:**

\`\`\`
orders → accounts → sales_reps → regions
  (via account_id) (via sales_rep_id) (via region_id)
\`\`\`

**The Syntax**

\`\`\`sql
SELECT 
  region.name,
  SUM(orders.amount) AS total,
  COUNT(*) AS count,
  ROUND(AVG(orders.amount), 2) AS avg
FROM orders
JOIN accounts ON ...
JOIN sales_reps ON ...
JOIN regions ON ...
GROUP BY region.name
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

Show each region's \`region_name\`, \`total_revenue\`, \`order_count\`, and \`avg_order_value\`. Order by total revenue descending.

</div>`,
        hint: `**💭 Pattern to Use:**

To join 4 tables and calculate multiple metrics:

\`\`\`sql
SELECT 
  table4.category_column,
  SUM(table1.amount) AS total,
  COUNT(table1.id) AS count,
  ROUND(AVG(table1.amount), 2) AS average
FROM table1
INNER JOIN table2 ON ...
INNER JOIN table3 ON ...
INNER JOIN table4 ON ...
GROUP BY table4.category_column
ORDER BY total DESC
\`\`\`

**For this question:**
- Chain 4 JOINs: orders → accounts → sales_reps → regions
- What's the matching column for each JOIN?
- GROUP BY which column from regions?
- Calculate 3 metrics: SUM, COUNT, AVG
- Use ROUND(AVG(...), 2) for currency formatting

**Tip:** Build incrementally - start with 1 JOIN, test, then add the next!`,
        hint2: `**⚡ The Answer:**

\`\`\`sql
SELECT 
  regions.region_name,
  SUM(orders.total_amount_usd) AS total_revenue,
  COUNT(orders.order_id) AS order_count,
  ROUND(AVG(orders.total_amount_usd), 2) AS avg_order_value
FROM orders
INNER JOIN accounts ON orders.account_id = accounts.account_id
INNER JOIN sales_reps ON accounts.sales_rep_id = sales_reps.sales_rep_id
INNER JOIN regions ON sales_reps.region_id = regions.region_id
GROUP BY regions.region_name
ORDER BY total_revenue DESC
\`\`\``,
        solution: `SELECT 
  regions.region_name,
  SUM(orders.total_amount_usd) AS total_revenue,
  COUNT(orders.order_id) AS order_count,
  ROUND(AVG(orders.total_amount_usd), 2) AS avg_order_value
FROM orders
INNER JOIN accounts ON orders.account_id = accounts.account_id
INNER JOIN sales_reps ON accounts.sales_rep_id = sales_reps.sales_rep_id
INNER JOIN regions ON sales_reps.region_id = regions.region_id
GROUP BY regions.region_name
ORDER BY total_revenue DESC`,
        tip: `Use ROUND(AVG(column), 2) to round averages to 2 decimal places for currency. This makes your output readable: $1234.56 instead of $1234.5647839.`,
        explanation: `**Four-Table JOIN Mastered!**

**What Just Happened:**

1. **orders:** Revenue data
2. **→ accounts:** Link to account owner
3. **→ sales_reps:** Get rep info
4. **→ regions:** Get region name
5. **GROUP BY region:** Aggregate by region
6. **Multiple metrics:** SUM, COUNT, AVG

**How Multiple Aggregates Work:**

- **SUM(amount):** Total revenue (Overall performance)
- **COUNT(*):** Order count (Volume/activity)
- **AVG(amount):** Avg order value (Deal quality)

**Strategic Analysis Framework:**

- **High revenue + High avg:** Premium market (Invest more)
- **High orders + Low avg:** Volume-driven (Train on upselling)
- **Low orders + High avg:** Big deal closers (Focus on lead gen)
- **Low across all:** Underperforming (Investigate root cause)

**This is Executive-Level Reporting:**

You've built the exact type of dashboard analysis that drives quarterly business reviews and strategy planning.`,
        concepts: [
            {
                icon: <FaDatabase className="text-3xl text-[#2563eb]" />,
                title: 'Multi-Table JOINs',
                description: 'Chain multiple INNER JOINs to connect data across many tables. Each JOIN uses ON to specify matching keys.'
            },
            {
                icon: <FaBoxes className="text-3xl text-[#f97316]" />,
                title: 'Multiple Metrics',
                description: 'Calculate several aggregates in one query: SUM for totals, COUNT for volume, AVG for averages. Provides comprehensive analysis.'
            },
            {
                icon: <FaLaptopCode className="text-3xl text-[#10b981]" />,
                title: 'ROUND()',
                description: 'ROUND(value, decimals) formats numbers for readability. Use ROUND(AVG(amount), 2) for currency values.'
            }
        ]
    }
];

export const paperPlusConcepts = [
    {
        icon: <FaLaptopCode className="text-3xl text-[#1e3a5f]" />,
        title: 'SELECT & FROM: Get Data',
        description: 'Every query starts here. SELECT chooses columns (* = all), FROM chooses the table. Foundation of all SQL queries.'
    },
    {
        icon: <FaSearch className="text-3xl text-[#fb923c]" />,
        title: 'WHERE: Filter Rows',
        description: 'WHERE filters rows before aggregation. Essential for targeted analysis like "Technology sector only" or "orders after 2024-01-01".'
    },
    {
        icon: <FaBoxes className="text-3xl text-[#2563eb]" />,
        title: 'GROUP BY: Aggregate Data',
        description: 'GROUP BY collapses rows and enables SUM, COUNT, AVG calculations. Transforms raw data into business insights.'
    },
    {
        icon: <FaTrophy className="text-3xl text-[#f97316]" />,
        title: 'Top N Pattern',
        description: 'GROUP BY + ORDER BY + LIMIT finds top performers. Most common analyst query: "Show me the top 10 by revenue".'
    },
    {
        icon: <FaDatabase className="text-3xl text-[#10b981]" />,
        title: 'Multi-Table JOINs',
        description: 'INNER JOIN connects tables where keys match. Chain multiple JOINs to combine data from across your database.'
    }
];
